import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, callGatewayBytes, type CallerIdentity } from "./gateway";
import type { AnkhInvoice, AnkhReceipt } from "./ankhInvoices";

/**
 * Issued: the invoices a seller raised ONE of its customers and the receipts
 * for that customer's payments (owner 2026-10-15: an io.ankhos login saw
 * "Not found" and no trace of what it had issued Arogyakundali). Read-only.
 *
 *   GET /billing/{ns}/accounts/{acct}/invoices                 radbilling GET /invoiceapi/{ns}/{acct}
 *   GET /billing/{ns}/accounts/{acct}/invoices/{InvoiceID}/pdf radbilling GET /invoiceapi/{ns}/{acct}/{id}/pdf
 *   GET /billing/{ns}/accounts/{acct}/receipts                 radbilling GET /receiptapi/{ns}/{acct}
 *   GET /billing/{ns}/accounts/{acct}/receipts/{OrderID}/pdf   radbilling GET /receiptapi/{ns}/{acct}/{order}/pdf
 *
 * 🔑 {ns} IS THE SELLER and is passed through unchanged (radaccess
 * ProxyToTenantBilling, the Record payment handler) -- the opposite of
 * ankhInvoices.ts, where {ns} is the buyer and the gateway rewrites to
 * io.ankhos. The customer list is /billing/{ns}/accounts (payments.ts).
 * Same grant as Record payment. The bodies are radbilling's model.Invoice and
 * model.Receipt, the same shapes the buyer view reads.
 */

const enc = encodeURIComponent;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The gateway forwards only canonical (lower-case) UUIDs; refuse anything else before it leaves the browser. */
function accountPath(ns: string, accountId: string): string {
  if (!UUID.test(accountId)) throw new Error(`Not an account id: ${accountId}`);
  return `/billing/${enc(ns)}/accounts/${accountId}`;
}

function docId(id: string, what: string): string {
  if (!UUID.test(id)) throw new Error(`Not ${what}: ${id}`);
  return id;
}

/**
 * radbilling answers an account with no invoices 404 " Records Not Found"
 * (model.ListInvoices), which is "none yet", not a failure. Only that answer:
 * a 404 from the gateway ("not a published billing route") still fails.
 */
export function isNoRecords(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404 && /records not found/i.test(e.message);
}

/** Newest first; [] when the customer has none. */
export async function listIssuedInvoices(
  identity: CallerIdentity,
  ns: string,
  accountId: string,
  signal?: AbortSignal,
): Promise<AnkhInvoice[]> {
  try {
    const r = await callGateway<AnkhInvoice[] | null>(
      identity,
      config().gatewayBase,
      `${accountPath(ns, accountId)}/invoices`,
      signal ? { signal } : {},
    );
    return [...(r.data ?? [])].sort((a, b) => b.createdat.localeCompare(a.createdat));
  } catch (e) {
    if (isNoRecords(e)) return [];
    throw e;
  }
}

/** The printable invoice. radbilling answers 409 for a Draft, which has no number yet. */
export async function getIssuedInvoicePdf(identity: CallerIdentity, ns: string, accountId: string, invoiceId: string): Promise<Blob> {
  return callGatewayBytes(
    identity,
    config().gatewayBase,
    `${accountPath(ns, accountId)}/invoices/${docId(invoiceId, "an invoice id")}/pdf`,
    { accept: "application/pdf" },
  );
}

/** Newest payment first, Cancelled included; [] when none. */
export async function listIssuedReceipts(
  identity: CallerIdentity,
  ns: string,
  accountId: string,
  signal?: AbortSignal,
): Promise<AnkhReceipt[]> {
  const r = await callGateway<AnkhReceipt[] | null>(
    identity,
    config().gatewayBase,
    `${accountPath(ns, accountId)}/receipts`,
    signal ? { signal } : {},
  );
  return r.data ?? [];
}

/** The printable receipt, keyed by the ORDER id (the payment), as radbilling keys it. */
export async function getIssuedReceiptPdf(identity: CallerIdentity, ns: string, accountId: string, orderId: string): Promise<Blob> {
  return callGatewayBytes(
    identity,
    config().gatewayBase,
    `${accountPath(ns, accountId)}/receipts/${docId(orderId, "a payment id")}/pdf`,
    { accept: "application/pdf" },
  );
}
